import { ErrorableLoader } from '@bordrio/ui-components';
import { Dialog } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { HiCheck } from 'react-icons/hi';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { Button } from '@src/components';
import Modal from '@src/components/Modal';
import Tabs from '@src/components/Tabs';
import { useGetOrdersQuery } from '@src/generated/graphql';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRequiredUserId } from '@src/hooks/useRequiredUserId';
import { EsignatureSuccessChecker } from './components/EsignatureSuccessChecker';
import OrderList from './components/OrderList';
import PaymentSuccessModal from './components/PaymentSuccessModal';
import VoucherList from './components/VoucherList';
import {
  ORDER_STATE_CONFIG,
  OrderRowWithInfo,
  getOrderRowWithInfo,
} from './utils/order-status';

export interface DashboardPageNavigationState {
  showIntakeSuccess?: boolean;
}

function DashboardPage(): JSX.Element {
  const { user, error: userError } = useCurrentUser();
  const userId = useRequiredUserId();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, error } = useGetOrdersQuery({
    variables: { userId },
  });
  const [searchParams] = useSearchParams();

  const { t } = useTranslation();

  // Intake Success Modal
  const locationState: DashboardPageNavigationState =
    (location.state as DashboardPageNavigationState) || {};

  if (!user || !data) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ErrorableLoader error={userError || error} />
      </div>
    );
  }

  const {
    nifOrders,
    fiscalRepSubscriptions,
    user: { orderVouchers: allOrderVouchers },
  } = data;

  function closeIntakeModal(): void {
    void navigate(location.pathname, { replace: true });
  }

  const orders = [...nifOrders, ...fiscalRepSubscriptions];

  const ordersWithStatus = orders.map((order) => getOrderRowWithInfo(order));
  function isOrderCompleted(order: OrderRowWithInfo): boolean {
    return (
      order.info.isInactive ?? ORDER_STATE_CONFIG[order.info.state].isInactive
    );
  }
  const pendingOrders = ordersWithStatus.filter(
    (order) => !isOrderCompleted(order),
  );
  const completedOrders = ordersWithStatus.filter(isOrderCompleted);

  const orderVouchers = allOrderVouchers.filter(
    (voucher) =>
      voucher.type !== 'IN_PERSON_BANK' && voucher.type !== 'REMOTE_BANK',
  );

  const hasOrders = orders.length > 0;
  const hasNoOrdersOrVouchers = !orders.length && !orderVouchers.length;

  return (
    <>
      {hasNoOrdersOrVouchers ? (
        <div className="flex flex-1 items-center justify-center px-8">
          <div className="max-w-md space-y-4 text-center">
            <h2>
              {t('dashboard.empty_state.introduction', 'Hi, {{name}}!', {
                name: user?.name || 'User',
              })}
            </h2>
            <p>
              {t(
                'dashboard.empty_state.description',
                'Ready to get started? Click the button below to create your first NIF order.',
              )}
            </p>
            <Button
              className="w-48 justify-center"
              onClick={() => navigate('orders/nif/place-order')}
            >
              {t('buttons.order_nif', 'Order a NIF')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 space-y-4 sm:space-y-8">
          <header>
            <div className="app-layout-wrapper">
              <h1>{t('dashboard.my_orders_title', 'My Orders')}</h1>
            </div>
          </header>
          <main>
            <div className="app-layout-wrapper space-y-8">
              <VoucherList vouchers={orderVouchers} />
              {hasOrders && (
                <Tabs
                  className="space-y-4"
                  defaultIndex={searchParams.get('showCompleted') ? 1 : 0}
                >
                  <Tabs.List>
                    <Tabs.Tab count={pendingOrders.length || undefined}>
                      {t('dashboard.tab.active', 'Active')}
                    </Tabs.Tab>
                    <Tabs.Tab count={completedOrders.length || undefined}>
                      {t('dashboard.tab.inactive', 'Inactive')}
                    </Tabs.Tab>
                  </Tabs.List>
                  <Tabs.Panels>
                    <Tabs.Panel>
                      <OrderList
                        orders={pendingOrders}
                        emptyStateText={t(
                          'dashboard.no_draft_orders',
                          'No orders in draft yet',
                        )}
                      />
                    </Tabs.Panel>
                    <Tabs.Panel>
                      <OrderList
                        orders={completedOrders}
                        emptyStateText={t(
                          'dashboard.no_completed_orders',
                          'No completed orders yet',
                        )}
                      />
                    </Tabs.Panel>
                  </Tabs.Panels>
                </Tabs>
              )}
            </div>
          </main>
        </div>
      )}

      <PaymentSuccessModal />
      <EsignatureSuccessChecker />

      <Modal
        isOpen={!!locationState.showIntakeSuccess}
        onClose={() => closeIntakeModal()}
      >
        <div>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <HiCheck className="h-6 w-6 text-green-600" aria-hidden="true" />
          </div>
          <div className="mt-3 text-center sm:mt-5">
            <Dialog.Title
              as="h3"
              className="text-lg font-medium leading-6 text-gray-900"
            >
              {t('dashboard.intake_received.title', 'Intake Successful!')}
            </Dialog.Title>
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                {t(
                  'dashboard.intake_received.description',
                  "Thank you for completing the intake form. We'll be in touch over email with next steps.",
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="mt-5 sm:mt-6">
          <Button
            className="w-full justify-center"
            onClick={() => closeIntakeModal()}
          >
            {t('dashboard.intake_received.see_orders_button', 'See my orders')}
          </Button>
        </div>
      </Modal>
    </>
  );
}

export default DashboardPage;
